import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useAuth } from '../context/AuthContext'
import {
  getManagerAnalytics,
  getSalesTargets,
  saveSalesTargets,
} from '../services/analytics_service'
import './ManagerInsights.css'

const COLORS = {
  accent: '#d1a050',
  teal: '#238d88',
  red: '#c0504d',
  blue: '#4a7fb5',
  purple: '#7a5ea8',
  grey: '#9aa6ab',
  grid: '#e2e8ea',
  muted: '#728087',
}
const PIE_COLORS = [
  COLORS.accent,
  COLORS.teal,
  COLORS.blue,
  COLORS.purple,
  COLORS.red,
  COLORS.grey,
  '#5b8c5a',
]
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'books', label: 'Books' },
  { id: 'executives', label: 'Sales executives' },
  { id: 'customers', label: 'Customers' },
]

const toIsoDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}
const defaultFilters = () => {
  const today = new Date()
  return {
    fromDate: toIsoDate(new Date(today.getFullYear(), 0, 1)),
    toDate: toIsoDate(today),
    orderType: '',
    dormantDays: '180',
  }
}

const currency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
const compactCurrency = (value) =>
  `GH₵${new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0)}`
const count = (value) => Number(value || 0).toLocaleString()
const percent = (value) => `${Number(value || 0).toFixed(1)}%`
const share = (part, whole) => (whole ? (part / whole) * 100 : 0)
const monthLabel = (period) => {
  const [year, month] = String(period).split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return Number.isNaN(date.getTime())
    ? period
    : date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}
const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const shortName = (value, length = 18) =>
  String(value || '').length > length
    ? `${String(value).slice(0, length - 1)}…`
    : value

const tooltipStyle = {
  border: `1px solid ${COLORS.grid}`,
  borderRadius: 6,
  fontSize: 12,
}

const AGEING_BUCKETS = [
  { key: 'd0_30', label: '0-30 days', color: COLORS.teal },
  { key: 'd31_60', label: '31-60 days', color: COLORS.accent },
  { key: 'd61_90', label: '61-90 days', color: COLORS.purple },
  { key: 'd90_plus', label: '90+ days', color: COLORS.red },
]

const yearOnYear = (current, previous) =>
  previous ? ((current - previous) / Math.abs(previous)) * 100 : null

const clickedRow = (entry) => entry?.payload ?? entry

const ordersListPath = (orderType) =>
  orderType === 'exercise_book'
    ? '/sales/orders/exercise-books'
    : '/sales/orders/publish-books'

function exportToExcel(data) {
  const workbook = XLSX.utils.book_new()
  const addSheet = (name, rows) =>
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(rows.length ? rows : [{ note: 'No data' }]),
      name,
    )

  addSheet(
    'Summary',
    Object.keys(data.summary).map((metric) => ({
      metric,
      this_period: data.summary[metric],
      same_period_last_year: data.previous?.[metric] ?? '',
    })),
  )
  addSheet('Monthly', data.monthly)
  addSheet('Sales executives', data.salesExecutives)
  addSheet('Debt ageing', data.debtAgeing.byExecutive)
  addSheet('Top customers', data.topCustomers)
  addSheet('Top debtors', data.topDebtors)
  addSheet('Dormant customers', data.dormantCustomers)
  addSheet('Customer types', data.customerTypes)
  addSheet('Top books', data.topBooks)
  addSheet('Slow movers', data.slowBooks)
  addSheet('High returns', data.highReturnBooks)
  addSheet('Reorder alerts', data.reorderAlerts)
  addSheet('Pipeline', data.pipeline)
  XLSX.writeFile(
    workbook,
    `manager-insights-${data.period.from}-to-${data.period.to}.xlsx`,
  )
}

function buildInsights(data) {
  const insights = []
  const { summary, salesExecutives, topCustomers, topDebtors, monthly } = data
  const revenue = summary.revenue

  const [leader] = salesExecutives
  if (leader && revenue) {
    insights.push({
      tone: 'info',
      text: `${leader.name} leads sales with ${currency(leader.revenue)} (${percent(share(leader.revenue, revenue))} of revenue).`,
    })
  }

  const rated = salesExecutives.filter((item) => item.revenue > 0)
  if (rated.length > 1) {
    const weakest = [...rated].sort(
      (a, b) => a.collectionRate - b.collectionRate,
    )[0]
    if (weakest.collectionRate < summary.collectionRate) {
      insights.push({
        tone: 'warn',
        text: `${weakest.name} has the lowest collection rate (${percent(weakest.collectionRate)}) against a company average of ${percent(summary.collectionRate)}. Review follow-ups on ${currency(weakest.outstanding)} outstanding.`,
      })
    }
  }

  const topTenRevenue = topCustomers.reduce(
    (sum, item) => sum + item.revenue,
    0,
  )
  if (revenue && topCustomers.length) {
    const concentration = share(topTenRevenue, revenue)
    insights.push({
      tone: concentration > 60 ? 'warn' : 'info',
      text: `Top ${topCustomers.length} customers generate ${percent(concentration)} of revenue${concentration > 60 ? ', a high dependency risk' : ''}.`,
    })
  }

  const [debtor] = topDebtors
  if (debtor) {
    insights.push({
      tone: 'warn',
      text: `${debtor.name} owes the most: ${currency(debtor.outstanding)} (${percent(debtor.collectionRate)} collected).`,
    })
  }

  const [bestBook] = data.topBooks
  if (bestBook && revenue) {
    insights.push({
      tone: 'good',
      text: `"${bestBook.title}" is the best seller with ${count(bestBook.units)} units and ${currency(bestBook.revenue)}. Keep stock levels healthy (${count(bestBook.inStock)} in stock).`,
    })
  }

  const overstocked = data.slowBooks.find((item) => item.inStock > item.units)
  if (overstocked) {
    insights.push({
      tone: 'warn',
      text: `"${overstocked.title}" sold only ${count(overstocked.units)} units but has ${count(overstocked.inStock)} in stock. Consider promotion before reprinting.`,
    })
  }

  const [highReturn] = data.highReturnBooks
  if (highReturn && highReturn.returnRate >= 10) {
    insights.push({
      tone: 'warn',
      text: `"${highReturn.title}" has a ${percent(highReturn.returnRate)} return rate. Investigate quality or over-supply.`,
    })
  }

  if (monthly.length > 1) {
    const last = monthly[monthly.length - 1]
    const previous = monthly[monthly.length - 2]
    if (previous.revenue) {
      const change = share(last.revenue - previous.revenue, previous.revenue)
      insights.push({
        tone: change >= 0 ? 'good' : 'warn',
        text: `Revenue in ${monthLabel(last.period)} is ${change >= 0 ? 'up' : 'down'} ${percent(Math.abs(change))} compared with ${monthLabel(previous.period)}.`,
      })
    }
  }

  const yearChange = data.previous?.revenue
    ? share(revenue - data.previous.revenue, data.previous.revenue)
    : null
  if (yearChange !== null) {
    insights.push({
      tone: yearChange >= 0 ? 'good' : 'warn',
      text: `Revenue is ${yearChange >= 0 ? 'up' : 'down'} ${percent(Math.abs(yearChange))} on the same period last year (${currency(data.previous.revenue)}).`,
    })
  }

  const ageingTotal = data.debtAgeing.totals.reduce(
    (sum, item) => sum + item.amount,
    0,
  )
  const overNinety = data.debtAgeing.totals.find(
    (item) => item.bucket === 'd90_plus',
  )
  if (ageingTotal && overNinety?.amount) {
    insights.push({
      tone: 'warn',
      text: `${currency(overNinety.amount)} (${percent(share(overNinety.amount, ageingTotal))} of all unpaid balances) is more than 90 days old.`,
    })
  }

  if (data.reorderAlerts.length) {
    insights.push({
      tone: 'warn',
      text: `${data.reorderAlerts.length} title${data.reorderAlerts.length === 1 ? ' has' : 's have'} less than 2 months of stock at the current sales rate. See Books for suggested print quantities.`,
    })
  }

  if (data.dormantCustomers.length) {
    const [biggest] = data.dormantCustomers
    insights.push({
      tone: 'info',
      text: `${data.dormantCustomers.length} past customers have not ordered in ${data.period.dormantDays} days, led by ${biggest.name} (${currency(biggest.lifetimeRevenue)} lifetime).`,
    })
  }

  const behindTarget = salesExecutives.filter(
    (item) => item.targetAchieved !== null && item.targetAchieved < 100,
  )
  if (behindTarget.length) {
    const furthest = [...behindTarget].sort(
      (a, b) => a.targetAchieved - b.targetAchieved,
    )[0]
    insights.push({
      tone: 'warn',
      text: `${behindTarget.length} executive${behindTarget.length === 1 ? ' is' : 's are'} behind target; ${furthest.name} is at ${percent(furthest.targetAchieved)}.`,
    })
  }

  return insights
}

function KpiCard({ label, value, detail, tone = 'neutral', delta, invert }) {
  const hasDelta = delta !== null && delta !== undefined
  const isGood = invert ? delta <= 0 : delta >= 0
  return (
    <article className={`insights-kpi insights-kpi--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
      {hasDelta && (
        <small
          className={`insights-kpi__delta insights-kpi__delta--${isGood ? 'good' : 'warn'}`}
        >
          {delta >= 0 ? '▲' : '▼'} {percent(Math.abs(delta))} vs last year
        </small>
      )}
    </article>
  )
}

function ChartCard({ title, subtitle, wide = false, children }) {
  return (
    <article className={`insights-card ${wide ? 'insights-card--wide' : ''}`}>
      <header className='insights-card__header'>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </header>
      {children}
    </article>
  )
}

function ChartBox({ height = 300, isEmpty, children }) {
  if (isEmpty) {
    return <div className='insights-empty'>No data for this period.</div>
  }
  return (
    <div className='insights-chart' style={{ height }}>
      <ResponsiveContainer width='100%' height='100%'>
        {children}
      </ResponsiveContainer>
    </div>
  )
}

function DataTable({ columns, rows, rowKey, onRowClick }) {
  if (!rows.length) {
    return <div className='insights-empty'>No data for this period.</div>
  }
  return (
    <div className='insights-table-wrap'>
      <table
        className={`insights-table ${onRowClick ? 'insights-table--clickable' : ''}`}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.numeric ? 'is-numeric' : undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row[rowKey] ?? 'row'}-${index}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => event.key === 'Enter' && onRowClick(row)
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.numeric ? 'is-numeric' : undefined}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RateBadge({ value }) {
  const tone = value >= 80 ? 'good' : value >= 60 ? 'info' : 'warn'
  return (
    <span className={`insights-badge insights-badge--${tone}`}>
      {percent(value)}
    </span>
  )
}

function OverviewTab({ data }) {
  const monthly = data.monthly.map((item) => ({
    ...item,
    label: monthLabel(item.period),
  }))
  return (
    <div className='insights-grid'>
      <ChartCard
        wide
        title='Revenue vs collections'
        subtitle='Monthly approved sales, cash collected and order volume'
      >
        <ChartBox height={320} isEmpty={!monthly.length}>
          <ComposedChart data={monthly} margin={{ top: 8, right: 8, left: 8 }}>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray='3 5' />
            <XAxis
              dataKey='label'
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis
              yAxisId='money'
              tickFormatter={compactCurrency}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
              width={70}
            />
            <YAxis
              yAxisId='orders'
              orientation='right'
              tick={{ fill: COLORS.muted, fontSize: 11 }}
              width={40}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name) =>
                name === 'Orders' ? count(value) : currency(value)
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              yAxisId='money'
              dataKey='revenue'
              name='Revenue'
              fill={COLORS.accent}
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId='money'
              dataKey='collected'
              name='Collected'
              fill={COLORS.teal}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId='orders'
              dataKey='orders'
              name='Orders'
              stroke={COLORS.blue}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ChartBox>
      </ChartCard>

      <ChartCard
        title='Order pipeline'
        subtitle='All orders raised in the period by status'
      >
        <ChartBox height={260} isEmpty={!data.pipeline.length}>
          <BarChart data={data.pipeline} margin={{ top: 8, right: 8, left: 0 }}>
            <CartesianGrid
              vertical={false}
              stroke={COLORS.grid}
              strokeDasharray='3 5'
            />
            <XAxis
              dataKey='status'
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis tick={{ fill: COLORS.muted, fontSize: 11 }} width={40} />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name, item) => [
                `${count(value)} orders (${currency(item.payload.value)})`,
                'Orders',
              ]}
            />
            <Bar dataKey='orders' radius={[4, 4, 0, 0]}>
              {data.pipeline.map((item, index) => (
                <Cell
                  key={item.status}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ChartBox>
      </ChartCard>

      <ChartCard
        title='Revenue by customer type'
        subtitle='Which segments drive sales'
      >
        <CustomerTypePie data={data.customerTypes} />
      </ChartCard>
    </div>
  )
}

function CustomerTypePie({ data }) {
  return (
    <ChartBox height={260} isEmpty={!data.length}>
      <PieChart>
        <Pie
          data={data}
          dataKey='revenue'
          nameKey='name'
          innerRadius={55}
          outerRadius={95}
          paddingAngle={2}
        >
          {data.map((item, index) => (
            <Cell
              key={item.name}
              fill={PIE_COLORS[index % PIE_COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(value) => currency(value)}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ChartBox>
  )
}

function BooksTab({ data, onOpenBook }) {
  const topBooks = data.topBooks.map((item) => ({
    ...item,
    name: shortName(item.title, 24),
  }))
  return (
    <div className='insights-grid'>
      <ChartCard
        wide
        title='Reorder alerts'
        subtitle='Titles with under 2 months of stock at the last 3 months sales rate. Suggested print covers 3 months.'
      >
        <DataTable
          rowKey='id'
          rows={data.reorderAlerts}
          onRowClick={onOpenBook}
          columns={[
            { key: 'title', label: 'Book' },
            {
              key: 'inStock',
              label: 'In stock',
              numeric: true,
              render: (row) => count(row.inStock),
            },
            {
              key: 'monthlyUnits',
              label: 'Sold / month',
              numeric: true,
              render: (row) => count(row.monthlyUnits),
            },
            {
              key: 'monthsOfCover',
              label: 'Months of cover',
              numeric: true,
              render: (row) => (
                <span
                  className={
                    row.monthsOfCover < 1 ? 'insights-text--warn' : undefined
                  }
                >
                  {Number(row.monthsOfCover).toFixed(1)}
                </span>
              ),
            },
            {
              key: 'suggestedPrint',
              label: 'Suggested print',
              numeric: true,
              render: (row) => <strong>{count(row.suggestedPrint)}</strong>,
            },
          ]}
        />
        {data.reorderAlerts.length > 0 && (
          <p className='insights-card__footnote'>
            <Link to='/admin/print-request'>Raise a print request →</Link>
          </p>
        )}
      </ChartCard>

      <ChartCard
        wide
        title='Top 10 books by revenue'
        subtitle='Best performers to prioritise for stock and reprints. Click a bar to open the book.'
      >
        <ChartBox height={360} isEmpty={!topBooks.length}>
          <BarChart
            data={topBooks}
            layout='vertical'
            margin={{ top: 0, right: 16, left: 8 }}
          >
            <CartesianGrid horizontal={false} stroke={COLORS.grid} />
            <XAxis
              type='number'
              tickFormatter={compactCurrency}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis
              type='category'
              dataKey='name'
              width={170}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name, item) => [
                `${currency(value)} · ${count(item.payload.units)} units`,
                'Revenue',
              ]}
              labelFormatter={(label, payload) =>
                payload?.[0]?.payload?.title || label
              }
            />
            <Bar
              dataKey='revenue'
              fill={COLORS.accent}
              radius={[0, 4, 4, 0]}
              barSize={16}
              cursor='pointer'
              onClick={(entry) => onOpenBook(clickedRow(entry))}
            />
          </BarChart>
        </ChartBox>
      </ChartCard>

      <ChartCard
        title='Slow movers'
        subtitle='Lowest unit sales in the period, compare with stock on hand'
      >
        <DataTable
          rowKey='id'
          rows={data.slowBooks}
          onRowClick={onOpenBook}
          columns={[
            { key: 'title', label: 'Book' },
            {
              key: 'units',
              label: 'Units sold',
              numeric: true,
              render: (row) => count(row.units),
            },
            {
              key: 'inStock',
              label: 'In stock',
              numeric: true,
              render: (row) => (
                <span
                  className={
                    row.inStock > row.units ? 'insights-text--warn' : undefined
                  }
                >
                  {count(row.inStock)}
                </span>
              ),
            },
          ]}
        />
      </ChartCard>

      <ChartCard
        title='Highest return rates'
        subtitle='Returned units as a share of units supplied'
      >
        <DataTable
          rowKey='id'
          rows={data.highReturnBooks}
          onRowClick={onOpenBook}
          columns={[
            { key: 'title', label: 'Book' },
            {
              key: 'returned',
              label: 'Returned',
              numeric: true,
              render: (row) => count(row.returned),
            },
            {
              key: 'returnRate',
              label: 'Rate',
              numeric: true,
              render: (row) => percent(row.returnRate),
            },
          ]}
        />
      </ChartCard>
    </div>
  )
}

function ExecutivesTab({
  data,
  onOpenExecutive,
  canManageTargets,
  onTargetsSaved,
}) {
  const executives = data.salesExecutives.map((item) => ({
    ...item,
    label: shortName(item.name, 16),
  }))
  const hasTargets = executives.some((item) => item.target > 0)
  const ageing = data.debtAgeing.byExecutive.map((item) => ({
    ...item,
    label: shortName(item.name, 16),
  }))
  return (
    <div className='insights-grid'>
      <ChartCard
        wide
        title='Sales executive performance'
        subtitle='Revenue, collections, outstanding balances and targets per executive. Click a bar to view their orders.'
      >
        <ChartBox height={340} isEmpty={!executives.length}>
          <BarChart data={executives} margin={{ top: 8, right: 8, left: 8 }}>
            <CartesianGrid
              vertical={false}
              stroke={COLORS.grid}
              strokeDasharray='3 5'
            />
            <XAxis
              dataKey='label'
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis
              tickFormatter={compactCurrency}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
              width={70}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => currency(value)}
              labelFormatter={(label, payload) =>
                payload?.[0]?.payload?.name || label
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey='revenue'
              name='Revenue'
              fill={COLORS.accent}
              radius={[4, 4, 0, 0]}
              cursor='pointer'
              onClick={(entry) => onOpenExecutive(clickedRow(entry))}
            />
            {hasTargets && (
              <Bar
                dataKey='target'
                name='Target'
                fill={COLORS.grey}
                radius={[4, 4, 0, 0]}
              />
            )}
            <Bar
              dataKey='collected'
              name='Collected'
              fill={COLORS.teal}
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey='outstanding'
              name='Outstanding'
              fill={COLORS.red}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartBox>
      </ChartCard>

      <ChartCard
        wide
        title='Executive scorecard'
        subtitle='Use target achievement, collection rate and average order value to guide coaching. Click a row to view orders.'
      >
        <DataTable
          rowKey='id'
          rows={data.salesExecutives}
          onRowClick={onOpenExecutive}
          columns={[
            { key: 'name', label: 'Sales executive' },
            {
              key: 'customers',
              label: 'Customers',
              numeric: true,
              render: (row) => count(row.customers),
            },
            {
              key: 'orders',
              label: 'Orders',
              numeric: true,
              render: (row) => count(row.orders),
            },
            {
              key: 'revenue',
              label: 'Revenue',
              numeric: true,
              render: (row) => currency(row.revenue),
            },
            {
              key: 'share',
              label: 'Share',
              numeric: true,
              render: (row) =>
                percent(share(row.revenue, data.summary.revenue)),
            },
            {
              key: 'target',
              label: 'Target',
              numeric: true,
              render: (row) => (row.target ? currency(row.target) : '-'),
            },
            {
              key: 'targetAchieved',
              label: 'Achieved',
              numeric: true,
              render: (row) =>
                row.targetAchieved === null ? (
                  '-'
                ) : (
                  <RateBadge value={row.targetAchieved} />
                ),
            },
            {
              key: 'averageOrderValue',
              label: 'Avg order',
              numeric: true,
              render: (row) => currency(row.averageOrderValue),
            },
            {
              key: 'outstanding',
              label: 'Outstanding',
              numeric: true,
              render: (row) => currency(row.outstanding),
            },
            {
              key: 'collectionRate',
              label: 'Collected',
              numeric: true,
              render: (row) => <RateBadge value={row.collectionRate} />,
            },
          ]}
        />
      </ChartCard>

      <ChartCard
        wide
        title='Debt ageing by executive'
        subtitle='All unpaid approved orders today, grouped by days since approval'
      >
        <div className='insights-ageing-totals'>
          {AGEING_BUCKETS.map((bucket) => {
            const total = data.debtAgeing.totals.find(
              (item) => item.bucket === bucket.key,
            )
            return (
              <div key={bucket.key} style={{ borderColor: bucket.color }}>
                <span>{bucket.label}</span>
                <strong>{currency(total?.amount)}</strong>
              </div>
            )
          })}
        </div>
        <ChartBox height={320} isEmpty={!ageing.length}>
          <BarChart data={ageing} margin={{ top: 8, right: 8, left: 8 }}>
            <CartesianGrid
              vertical={false}
              stroke={COLORS.grid}
              strokeDasharray='3 5'
            />
            <XAxis
              dataKey='label'
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis
              tickFormatter={compactCurrency}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
              width={70}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => currency(value)}
              labelFormatter={(label, payload) =>
                payload?.[0]?.payload?.name || label
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {AGEING_BUCKETS.map((bucket) => (
              <Bar
                key={bucket.key}
                dataKey={bucket.key}
                name={bucket.label}
                stackId='ageing'
                fill={bucket.color}
              />
            ))}
          </BarChart>
        </ChartBox>
      </ChartCard>

      {canManageTargets && (
        <ChartCard
          wide
          title='Monthly sales targets'
          subtitle='Set revenue targets per executive. Targets for every month in the selected period are added together.'
        >
          <TargetsEditor onSaved={onTargetsSaved} />
        </ChartCard>
      )}
    </div>
  )
}

function TargetsEditor({ onSaved }) {
  const [period, setPeriod] = useState(() => toIsoDate(new Date()).slice(0, 7))
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!period) return undefined
    const controller = new AbortController()
    setIsLoading(true)
    setError('')
    setMessage('')
    getSalesTargets(period, controller.signal)
      .then((result) =>
        setRows(
          result.targets.map((item) => ({
            ...item,
            value: item.target === null ? '' : String(item.target),
          })),
        ),
      )
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [period])

  const updateRow = (salesExecutiveId, value) => {
    setRows((current) =>
      current.map((row) =>
        row.salesExecutiveId === salesExecutiveId ? { ...row, value } : row,
      ),
    )
  }

  const save = async (event) => {
    event.preventDefault()
    const targets = rows
      .filter((row) => row.value !== '')
      .map((row) => ({
        sales_executive_id: row.salesExecutiveId,
        target_amount: Number(row.value),
      }))
    if (!targets.length) {
      setError('Enter at least one target amount.')
      return
    }
    setIsSaving(true)
    setError('')
    setMessage('')
    try {
      await saveSalesTargets(period, targets)
      setMessage(`Targets for ${monthLabel(period)} saved.`)
      onSaved()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form className='insights-targets' onSubmit={save}>
      <label className='insights-targets__period'>
        Month
        <input
          type='month'
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
          required
        />
      </label>
      {isLoading ? (
        <div className='insights-empty'>Loading targets...</div>
      ) : (
        <div className='insights-targets__grid'>
          {rows.map((row) => (
            <label key={row.salesExecutiveId}>
              {row.name}
              <input
                type='number'
                min='0'
                step='0.01'
                placeholder='No target'
                value={row.value}
                onChange={(event) =>
                  updateRow(row.salesExecutiveId, event.target.value)
                }
              />
            </label>
          ))}
        </div>
      )}
      {error && (
        <div className='insights-page__error' role='alert'>
          {error}
        </div>
      )}
      {message && (
        <div className='insights-targets__message' role='status'>
          {message}
        </div>
      )}
      <button
        className='insights-button insights-button--primary'
        type='submit'
        disabled={isSaving || isLoading || !rows.length}
      >
        {isSaving ? 'Saving...' : 'Save targets'}
      </button>
    </form>
  )
}

function CustomersTab({ data, onOpenCustomer }) {
  const topCustomers = data.topCustomers.map((item) => ({
    ...item,
    label: shortName(item.name, 24),
  }))
  return (
    <div className='insights-grid'>
      <ChartCard title='Top 10 customers' subtitle='Key accounts by revenue'>
        <ChartBox height={360} isEmpty={!topCustomers.length}>
          <BarChart
            data={topCustomers}
            layout='vertical'
            margin={{ top: 0, right: 16, left: 8 }}
          >
            <CartesianGrid horizontal={false} stroke={COLORS.grid} />
            <XAxis
              type='number'
              tickFormatter={compactCurrency}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <YAxis
              type='category'
              dataKey='label'
              width={160}
              tick={{ fill: COLORS.muted, fontSize: 11 }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => currency(value)}
              labelFormatter={(label, payload) =>
                payload?.[0]?.payload?.name || label
              }
            />
            <Bar
              dataKey='revenue'
              name='Revenue'
              fill={COLORS.teal}
              radius={[0, 4, 4, 0]}
              barSize={16}
              cursor='pointer'
              onClick={(entry) => onOpenCustomer(clickedRow(entry))}
            />
          </BarChart>
        </ChartBox>
      </ChartCard>

      <ChartCard
        title='Customer segments'
        subtitle='Revenue and active customers by type'
      >
        <CustomerTypePie data={data.customerTypes} />
        <DataTable
          rowKey='name'
          rows={data.customerTypes}
          columns={[
            { key: 'name', label: 'Type' },
            {
              key: 'customers',
              label: 'Customers',
              numeric: true,
              render: (row) => count(row.customers),
            },
            {
              key: 'revenue',
              label: 'Revenue',
              numeric: true,
              render: (row) => currency(row.revenue),
            },
          ]}
        />
      </ChartCard>

      <ChartCard
        wide
        title='Largest outstanding balances'
        subtitle='Prioritise these accounts for collection'
      >
        <DataTable
          rowKey='id'
          rows={data.topDebtors}
          onRowClick={onOpenCustomer}
          columns={[
            { key: 'name', label: 'Customer' },
            { key: 'type', label: 'Type' },
            { key: 'salesExecutive', label: 'Sales executive' },
            {
              key: 'lastOrder',
              label: 'Last order',
              render: (row) => formatDate(row.lastOrder),
            },
            {
              key: 'revenue',
              label: 'Billed',
              numeric: true,
              render: (row) => currency(row.revenue),
            },
            {
              key: 'outstanding',
              label: 'Outstanding',
              numeric: true,
              render: (row) => currency(row.outstanding),
            },
            {
              key: 'collectionRate',
              label: 'Collected',
              numeric: true,
              render: (row) => <RateBadge value={row.collectionRate} />,
            },
          ]}
        />
      </ChartCard>

      <ChartCard
        wide
        title='Dormant customers'
        subtitle={`Past customers with no approved order in the last ${data.period.dormantDays} days, ranked by lifetime revenue. Good candidates for a follow-up visit.`}
      >
        <DataTable
          rowKey='id'
          rows={data.dormantCustomers}
          onRowClick={onOpenCustomer}
          columns={[
            { key: 'name', label: 'Customer' },
            { key: 'type', label: 'Type' },
            { key: 'salesExecutive', label: 'Sales executive' },
            {
              key: 'lastOrder',
              label: 'Last order',
              render: (row) => formatDate(row.lastOrder),
            },
            {
              key: 'orders',
              label: 'Orders',
              numeric: true,
              render: (row) => count(row.orders),
            },
            {
              key: 'lifetimeRevenue',
              label: 'Lifetime revenue',
              numeric: true,
              render: (row) => currency(row.lifetimeRevenue),
            },
            {
              key: 'outstanding',
              label: 'Outstanding',
              numeric: true,
              render: (row) => currency(row.outstanding),
            },
          ]}
        />
      </ChartCard>
    </div>
  )
}

function ManagerInsights() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const [filters, setFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(filters)
  const [refreshKey, setRefreshKey] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')
  const [isPrinting, setIsPrinting] = useState(false)
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    setError('')
    getManagerAnalytics(appliedFilters, controller.signal)
      .then(setData)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [appliedFilters, refreshKey])

  useEffect(() => {
    if (!isPrinting) return undefined
    // Give the charts on every tab a moment to render before printing.
    const timer = window.setTimeout(() => {
      window.print()
      setIsPrinting(false)
    }, 600)
    return () => window.clearTimeout(timer)
  }, [isPrinting])

  const openBook = (book) => {
    if (!book?.id) return
    const base =
      book.type === 'exercise_book'
        ? '/admin/books/exercise-books'
        : '/admin/books/publish-books'
    navigate(`${base}/${book.id}`)
  }
  const openCustomer = (customer) => {
    if (customer?.id) navigate(`/customers/${customer.id}`)
  }
  const openExecutive = (executive) => {
    if (!executive?.id) return
    const params = new URLSearchParams({
      salesExecutive: String(executive.id),
      fromDate: appliedFilters.fromDate,
      toDate: appliedFilters.toDate,
    })
    navigate(`${ordersListPath(appliedFilters.orderType)}?${params}`)
  }

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({ ...current, [name]: value }))
  }
  const applyFilters = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
  }
  const resetFilters = () => {
    const initial = defaultFilters()
    setFilters(initial)
    setAppliedFilters(initial)
  }

  const summary = data?.summary
  const previous = data?.previous
  const insights = data ? buildInsights(data) : []
  const showTab = (id) => isPrinting || activeTab === id

  return (
    <div className='insights-page'>
      <nav className='insights-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <span>Reports</span>
        <span aria-hidden='true'>/</span>
        <strong>Manager insights</strong>
      </nav>
      <header className='insights-page__header'>
        <div>
          <span className='app-content__eyebrow'>Decision support</span>
          <h1>Manager insights</h1>
          <p>
            Track book performance, sales executive results and customer value
            to guide stocking, targets and collections.
          </p>
        </div>
      </header>

      <form className='insights-filters' onSubmit={applyFilters}>
        <label>
          From
          <input
            type='date'
            name='fromDate'
            value={filters.fromDate}
            max={filters.toDate}
            onChange={updateFilter}
            required
          />
        </label>
        <label>
          To
          <input
            type='date'
            name='toDate'
            value={filters.toDate}
            min={filters.fromDate}
            onChange={updateFilter}
            required
          />
        </label>
        <label>
          Book type
          <select
            name='orderType'
            value={filters.orderType}
            onChange={updateFilter}
          >
            <option value=''>All books</option>
            <option value='published_book'>Published books</option>
            <option value='exercise_book'>Exercise books</option>
          </select>
        </label>
        <label>
          Dormant after
          <select
            name='dormantDays'
            value={filters.dormantDays}
            onChange={updateFilter}
          >
            <option value='90'>90 days</option>
            <option value='180'>180 days</option>
            <option value='365'>1 year</option>
          </select>
        </label>
        <div className='insights-filters__actions'>
          <button
            className='insights-button insights-button--primary'
            type='submit'
            disabled={isLoading}
          >
            Apply
          </button>
          <button
            className='insights-button'
            type='button'
            onClick={resetFilters}
            disabled={isLoading}
          >
            Reset
          </button>
          <button
            className='insights-button'
            type='button'
            onClick={() => exportToExcel(data)}
            disabled={!data || isLoading}
          >
            Export Excel
          </button>
          <button
            className='insights-button'
            type='button'
            onClick={() => setIsPrinting(true)}
            disabled={!data || isLoading || isPrinting}
          >
            Print / PDF
          </button>
        </div>
      </form>

      {error && (
        <div className='insights-page__error' role='alert'>
          {error}
        </div>
      )}

      {isLoading && !data ? (
        <div className='insights-page__loading'>Loading insights...</div>
      ) : (
        summary && (
          <div
            className={
              isLoading
                ? 'insights-page__body is-loading'
                : 'insights-page__body'
            }
          >
            <section
              className='insights-kpis'
              aria-label='Key performance indicators'
            >
              <KpiCard
                label='Revenue'
                value={currency(summary.revenue)}
                detail={`${count(summary.orders)} approved orders`}
                tone='accent'
                delta={yearOnYear(summary.revenue, previous?.revenue)}
              />
              <KpiCard
                label='Collected'
                value={currency(summary.collected)}
                detail={`${percent(summary.collectionRate)} collection rate`}
                tone='good'
                delta={yearOnYear(summary.collected, previous?.collected)}
              />
              <KpiCard
                label='Outstanding'
                value={currency(summary.outstanding)}
                detail='Unpaid balances'
                tone='warn'
                delta={yearOnYear(summary.outstanding, previous?.outstanding)}
                invert
              />
              <KpiCard
                label='Average order'
                value={currency(summary.averageOrderValue)}
                detail='Revenue per order'
                delta={yearOnYear(
                  summary.averageOrderValue,
                  previous?.averageOrderValue,
                )}
              />
              <KpiCard
                label='Active customers'
                value={count(summary.customers)}
                detail='Placed approved orders'
                delta={yearOnYear(summary.customers, previous?.customers)}
              />
              <KpiCard
                label='Units sold'
                value={count(summary.units)}
                detail={`${percent(summary.returnRate)} returned`}
                delta={yearOnYear(summary.units, previous?.units)}
              />
            </section>

            {insights.length > 0 && (
              <section
                className='insights-highlights'
                aria-label='Key findings'
              >
                <h2>Key findings</h2>
                <ul>
                  {insights.map((item) => (
                    <li
                      key={item.text}
                      className={`insights-highlight insights-highlight--${item.tone}`}
                    >
                      {item.text}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div
              className='insights-tabs'
              role='tablist'
              aria-label='Insight categories'
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  aria-selected={activeTab === tab.id}
                  className={activeTab === tab.id ? 'is-active' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {showTab('overview') && <OverviewTab data={data} />}
            {showTab('books') && <BooksTab data={data} onOpenBook={openBook} />}
            {showTab('executives') && (
              <ExecutivesTab
                data={data}
                onOpenExecutive={openExecutive}
                canManageTargets={hasPermission('manageTargets') && !isPrinting}
                onTargetsSaved={() => setRefreshKey((key) => key + 1)}
              />
            )}
            {showTab('customers') && (
              <CustomersTab data={data} onOpenCustomer={openCustomer} />
            )}
          </div>
        )
      )}
    </div>
  )
}

export default ManagerInsights
